import { useEffect, useState, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { useSelector, useDispatch } from "react-redux";
import {
  globalStyle,
  handleDangerVisible,
  handleVisible,
} from "../../global/Utils";
import { getReceiving, deleteReceiving } from "../../Actions/ReceivingAction";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { IconButton, Tooltip, Box } from "@mui/material";
import Swal from "sweetalert2";
import { isUndefined, debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import MobileViewTabelList from "../Common/MobileViewTabelList";
import { formatQty } from "../../global/stock";
import { formatTwoDecimals } from "../../global/money";

/**
 * Receivings, newest first (the endpoint sorts; nothing is re-sorted here) and
 * searchable by number, supplier, bill number or item name.
 */
const ReceivingList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [globalFilter, setGlobalFilter] = useState("");

  const isGettingReceivingList = useSelector(
    (state) => state.ReceivingReducer?.isGettingReceivingList,
  );
  const receivingList = useSelector(
    (state) => state.ReceivingReducer?.receivingList || [],
  );
  const totalResults = useSelector(
    (state) => state.ReceivingReducer?.totalResults || 0,
  );

  useEffect(() => {
    dispatch(
      getReceiving(pagination.pageIndex, pagination.pageSize, globalFilter),
    );
  }, [pagination, dispatch, globalFilter]);

  const debouncedSetFilter = useMemo(
    () => debounce((value) => setGlobalFilter(value), 500),
    [],
  );

  useEffect(() => () => debouncedSetFilter.cancel(), [debouncedSetFilter]);

  const handleDeleteReceiving = (row) => {
    Swal.fire({
      title: "Are you sure?",
      // Deleting is not just removing a record - it takes the received stock
      // back out, which the server refuses if the goods have been sold on.
      text: "The stock this receiving added will be taken back out.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      customClass: { popup: "mui-swal-popup" },
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        if (container) container.style.zIndex = "2000";
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          deleteReceiving(
            row.original._id,
            handleVisible,
            handleDangerVisible,
            pagination.pageIndex,
            pagination.pageSize,
            globalFilter,
          ),
        );
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "#",
        size: 60,
        enableResizing: false,
        enableEditing: false,
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize,
      },
      {
        accessorKey: "receivingNumber",
        header: "Receiving No.",
        size: 140,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "date",
        header: "Date",
        size: 120,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "supplierName",
        header: "Supplier",
        size: 180,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "referenceNo",
        header: "Bill No.",
        size: 130,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "totalQuantity",
        header: "Total Qty",
        size: 110,
        grow: 1,
        enableEditing: false,
        Cell: ({ row }) => formatQty(row.original?.totalQuantity),
      },
      {
        accessorKey: "totalCost",
        header: "Total Cost",
        size: 130,
        grow: 1,
        enableEditing: false,
        Cell: ({ row }) => `Rs ${formatTwoDecimals(row.original?.totalCost)}`,
      },
      {
        // Admins see the whole book, so the owner is what tells one user's
        // receivings from another's. A user only ever sees their own, where the
        // column is simply their own address - the same way the item list and
        // the invoice list carry it.
        accessorKey: "createdBy.email",
        header: "Created By",
        size: 200,
        grow: 1,
        enableEditing: false,
      },
      {
        id: "actions",
        header: "Actions",
        size: 140,
        grow: 0,
        enableEditing: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box display="flex" gap={1}>
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() =>
                  navigate("/main/view-receiving-details", {
                    state: row.original,
                  })
                }
                sx={{ color: "black" }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() =>
                  navigate("/main/edit-receiving", { state: row.original })
                }
                sx={{ color: "black" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteReceiving(row)}
                sx={{ color: "black" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line
    [pagination],
  );

  const renderMobileCards = () =>
    receivingList.map((receiving, index) => {
      const serial = index + 1 + pagination.pageIndex * pagination.pageSize;
      return (
        <Box
          key={receiving._id}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box sx={{ fontSize: 12, color: "#999" }}>#{serial}</Box>
            <Box display="flex" gap={1}>
              <Tooltip title="View">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate("/main/view-receiving-details", {
                      state: receiving,
                    })
                  }
                  sx={{ color: "black" }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate("/main/edit-receiving", { state: receiving })
                  }
                  sx={{ color: "black" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() =>
                    handleDeleteReceiving({ original: receiving })
                  }
                  sx={{ color: "black" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <b>Receiving No:</b>
            <Box sx={{ fontSize: 14 }}>{receiving.receivingNumber || "-"}</Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <b>Date:</b>
            <Box sx={{ fontSize: 14 }}>{receiving.date || "-"}</Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <b>Supplier:</b>
            <Box sx={{ fontSize: 14 }}>{receiving.supplierName || "-"}</Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <b>Total Qty:</b>
            <Box sx={{ fontSize: 14 }}>
              {formatQty(receiving.totalQuantity)}
            </Box>
          </Box>
          <Box>
            <b>Total Cost:</b>
            <Box sx={{ fontSize: 14 }}>
              Rs {formatTwoDecimals(receiving.totalCost)}
            </Box>
          </Box>
        </Box>
      );
    });

  return (
    <Box className="p-4">
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Box
            component="h1"
            sx={{
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 700,
              color: globalStyle.bgColorBlue,
              fontSize: {
                xs: globalStyle.headingFontSizeSmall,
                sm: globalStyle.headingFontSize,
              },
              lineHeight: 1.2,
            }}
          >
            Receivings List
          </Box>
          <Box
            component="p"
            sx={{
              mt: 0.5,
              fontFamily: globalStyle.fontFamilyParagraph,
              fontSize: { xs: 12, sm: 14 },
              color: "#666",
            }}
          >
            Goods received from suppliers - each one adds to stock
          </Box>
        </Box>
      </Box>

      {isMobile ? (
        <MobileViewTabelList
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pagination={pagination}
          setPagination={setPagination}
          totalResults={totalResults}
          loading={isGettingReceivingList}
          searchLabel="Receivings"
        >
          {renderMobileCards()}
        </MobileViewTabelList>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={receivingList}
          enableRowActions={false}
          manualPagination
          rowCount={totalResults}
          state={{
            columnOrder: [
              "_id",
              "receivingNumber",
              "date",
              "supplierName",
              "referenceNo",
              "totalQuantity",
              "totalCost",
              "createdBy.email",
              "actions",
            ],
            columnPinning: { right: ["actions"] },
            isLoading: isGettingReceivingList,
            pagination,
          }}
          onPaginationChange={setPagination}
          onGlobalFilterChange={(value) =>
            isUndefined(value)
              ? debouncedSetFilter("")
              : debouncedSetFilter(value)
          }
          columnFilterDisplayMode={false}
          muiTablePaperProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              borderRadius: `${globalStyle.borderRadius}px`,
              fontFamily: globalStyle.fontFamily,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: globalStyle.blueColor,
              color: "#fff",
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 600,
              borderBottom: "none",
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyParagraph,
              color: "#333",
              borderBottom: `1px solid ${globalStyle.curveLightColor}`,
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              "&:hover": { backgroundColor: globalStyle.bgColor },
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: "none",
              overflow: "visible",
              borderRadius: `${globalStyle.borderRadius}px`,
            },
          }}
          muiPaginationProps={{
            sx: {
              "& .MuiTablePagination-toolbar": {
                fontFamily: globalStyle.fontFamily,
              },
              "& .Mui-selected": {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
              },
            },
          }}
          muiTableProps={{ sx: { tableLayout: "fixed" } }}
        />
      )}
    </Box>
  );
};

export default ReceivingList;
